import { Client as BrowserClient, Account as BrowserAccount } from "appwrite"
import {
  PROXY_REQUEST_TIMEOUT_MS,
  classifyFetchError,
  retryDelayMs,
  shouldRetry,
  type ProxyFailure,
} from "@/lib/proxy-retry"
import { ProxyRequestError } from "@/lib/errors"

// Environment variables (Next.js injects NEXT_PUBLIC_ vars at build time).
// Fallbacks to Cloud values were removed deliberately: when the project migrates
// from Cloud to self-hosted (Phase 2), a missing env var must fail loudly rather
// than silently point at Cloud and leak traffic/data to the wrong backend.
function requireEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Set it in .env.local (dev) or the ` +
        "deployment environment. Cloud fallbacks were removed to prevent " +
        "silent wrong-backend wiring at migration cutover.",
    )
  }
  return value
}

export const ENDPOINT = requireEnv(
  "NEXT_PUBLIC_APPWRITE_ENDPOINT",
  process.env.NEXT_PUBLIC_APPWRITE_ENDPOINT,
)
export const PROJECT_ID = requireEnv(
  "NEXT_PUBLIC_APPWRITE_PROJECT_ID",
  process.env.NEXT_PUBLIC_APPWRITE_PROJECT_ID,
)
const DATABASE_ID_RAW = requireEnv(
  "NEXT_PUBLIC_APPWRITE_DATABASE_ID",
  process.env.NEXT_PUBLIC_APPWRITE_DATABASE_ID,
)

// Browser client for real-time subscriptions and session management (client-side only).
// `setEndpointRealtime` lets the Phase-2 self-hosted deployment point realtime at a
// different host (e.g. an internal LAN websocket gateway) than the REST endpoint.
// When the env var is absent — the Phase-1 Cloud default — the SDK derives the
// realtime URL from the REST endpoint automatically, so existing behavior is
// unchanged.
const REALTIME_ENDPOINT = process.env.NEXT_PUBLIC_APPWRITE_REALTIME_ENDPOINT
export const client = (() => {
  const c = new BrowserClient().setEndpoint(ENDPOINT).setProject(PROJECT_ID)
  if (REALTIME_ENDPOINT) c.setEndpointRealtime(REALTIME_ENDPOINT)
  return c
})()

export const account = new BrowserAccount(client)

/**
 * Proxy class that routes all database operations through our own API
 * to avoid CORS issues with direct browser-to-Appwrite requests.
 * All 37+ client components that import `databases` use this transparently.
 */
class DatabasesProxy {
  // Runaway-guardrail backoff: max 3 retries, total wait ≤ ~6s.
  // Server signals backoff via 200 + `{ retryMs: N }` (never a 429 mid-service).
  private static readonly MAX_GUARDRAIL_RETRIES = 3

  // Return type matches the dynamic response shape of /api/db; individual public
  // methods cast to their concrete return type. The original signature was
  // implicit `any` for the same reason — we can't statically enumerate every
  // action payload here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async request(body: Record<string, unknown>, retriesLeft = DatabasesProxy.MAX_GUARDRAIL_RETRIES): Promise<any> {
    const res = await this.fetchWithRetry(body)
    if (!res.ok) {
      // Expired JWT or invalid session — redirect to login instead of showing cryptic error
      if (res.status === 401 && typeof window !== "undefined") {
        // Deliberate FULL navigation, not a router push: the JWT is dead and the
        // whole client state (queries, realtime subscriptions, drafts in memory)
        // must be discarded with it. This is a plain module, not a component, so
        // there is no router here. Absolute URL so a future basePath cannot turn
        // this into a relative-destination bug (@next/next rule).
        window.location.assign(new URL("/login", window.location.origin).href)
        throw new Error("Session expired — redirecting to login")
      }
      const err = (await res.json().catch(() => ({ error: "Request failed" }))) as {
        error?: unknown
        code?: unknown
      }
      // Typed so callers can branch on the HTTP status (409 = optimistic-lock
      // conflict) instead of matching message text; the message is unchanged.
      throw new ProxyRequestError(
        typeof err.error === "string" && err.error ? err.error : `Database operation failed (${res.status})`,
        res.status,
        typeof err.code === "number" ? err.code : undefined,
      )
    }
    const json = await res.json()
    // Runaway-loop backoff signal from server. Respect it silently so the UI
    // stays responsive during an honest client bug.
    if (json && typeof json === "object" && typeof (json as { retryMs?: unknown }).retryMs === "number") {
      const retryMs = (json as { retryMs: number }).retryMs
      if (retriesLeft > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryMs))
        return this.request(body, retriesLeft - 1)
      }
      // Give up after max retries — surface a generic error the UI can show.
      throw new Error("The system is temporarily busy. Please try again in a moment.")
    }
    return json
  }

  /**
   * Execute the proxy fetch with a hard timeout, plus bounded retry for
   * transient failures on idempotent READ actions only (policy + tests:
   * `lib/proxy-retry.ts`). Writes are never retried here — a timed-out write
   * may have committed server-side (double-submit risk on money paths).
   *
   * Any HTTP response that isn't a retryable read failure is returned as-is,
   * so the caller's existing handling (401 → /login redirect, structured
   * error JSON, runaway-guardrail `retryMs` on 200s) is untouched. This retry
   * never sees a guardrail signal (a 200 body field) and the guardrail loop
   * never sees a network failure, so the two backoffs cannot stack.
   */
  private async fetchWithRetry(body: Record<string, unknown>): Promise<Response> {
    const action = body.action
    for (let attempt = 0; ; attempt++) {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), PROXY_REQUEST_TIMEOUT_MS)
      try {
        const res = await fetch("/api/db", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
          signal: controller.signal,
        })
        if (res.ok || !shouldRetry(action, { kind: "http", status: res.status }, attempt)) {
          return res
        }
      } catch (err) {
        const failure: ProxyFailure = classifyFetchError(err)
        if (!shouldRetry(action, failure, attempt)) {
          // Structured, user-showable errors — never the raw fetch rejection.
          throw new Error(
            failure.kind === "timeout"
              ? "The request timed out. Please check your connection and try again."
              : "Network error. Please check your connection and try again.",
          )
        }
      } finally {
        clearTimeout(timer)
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs(attempt)))
    }
  }

  /**
   * List documents through the proxy.
   *
   * Default behavior — `{ total, documents }`. Drop-in compatible with the
   * Appwrite SDK shape; every existing caller stays unchanged.
   *
   * Pass `options.total: false` to opt out of the count. The response shape
   * becomes `{ documents }` only. Useful with `Query.cursorAfter(...)` for
   * paginated reads of large collections (reports, audit logs).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async listDocuments<T = any>(
    databaseId: string,
    collectionId: string,
    queries: string[] | undefined,
    options: { total: false },
  ): Promise<{ documents: T[] }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async listDocuments<T = any>(
    databaseId: string,
    collectionId: string,
    queries?: string[],
    options?: { total?: boolean },
  ): Promise<{ total: number; documents: T[] }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async listDocuments<T = any>(
    databaseId: string,
    collectionId: string,
    queries?: string[],
    options?: { total?: boolean },
  ): Promise<{ total?: number; documents: T[] }> {
    return this.request({
      action: "list",
      databaseId,
      collectionId,
      queries,
      ...(options?.total === false ? { total: false } : {}),
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getDocument<T = any>(databaseId: string, collectionId: string, documentId: string, queries?: string[]): Promise<T> {
    return this.request({ action: "get", databaseId, collectionId, documentId, queries })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createDocument<T = any>(databaseId: string, collectionId: string, documentId: string, data: Record<string, unknown>, permissions?: string[]): Promise<T> {
    return this.request({ action: "create", databaseId, collectionId, documentId, data, permissions })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateDocument<T = any>(databaseId: string, collectionId: string, documentId: string, data?: Record<string, unknown>, permissions?: string[], expectedUpdatedAt?: string): Promise<T> {
    return this.request({ action: "update", databaseId, collectionId, documentId, data, permissions, expectedUpdatedAt })
  }

  async deleteDocument(databaseId: string, collectionId: string, documentId: string): Promise<void> {
    return this.request({ action: "delete", databaseId, collectionId, documentId })
  }

  /**
   * Atomically create multiple documents in a single transaction.
   * All succeed or all fail — no partial state.
   */
  async batchCreate(
    databaseId: string,
    operations: Array<{ collectionId: string; documentId: string; data: Record<string, unknown> }>
  ): Promise<{ success: boolean; count: number; transactionId: string }> {
    return this.request({ action: "batch_create", databaseId, operations })
  }

  /** Atomically increment a numeric column. No read-modify-write race. */
  async incrementColumn(
    databaseId: string, collectionId: string, documentId: string,
    column: string, value = 1, max?: number
  ): Promise<void> {
    return this.request({ action: "increment", databaseId, collectionId, documentId, column, value, max })
  }

  /**
   * Atomically allocate the next integer in a named sequence (the `counters`
   * collection, one row per sequence). The server increments and reads the
   * row's `value` in a single op — collision-free across concurrent callers
   * and sessions, unlike a client read-then-increment. Used for receipt /
   * PTS numbers where two callers in the same instant must never collide.
   */
  async nextSequence(name: string): Promise<number> {
    const r = await this.request({ action: "next_sequence", databaseId: DATABASE_ID, name })
    return (r as { value: number }).value
  }

  /** Atomically decrement a numeric column. No read-modify-write race. */
  async decrementColumn(
    databaseId: string, collectionId: string, documentId: string,
    column: string, value = 1, min?: number
  ): Promise<void> {
    return this.request({ action: "decrement", databaseId, collectionId, documentId, column, value, min })
  }

  /** Create or update a document by ID. */
  async upsertDocument<T = unknown>(
    databaseId: string, collectionId: string, documentId: string,
    data: Record<string, unknown>
  ): Promise<T> {
    return this.request({ action: "upsert", databaseId, collectionId, documentId, data })
  }

  /**
   * Execute a mixed transaction: create, update, upsert, delete, increment, decrement
   * in a single atomic commit. All succeed or all fail.
   */
  async transaction(
    databaseId: string,
    operations: Array<{
      action: "create" | "update" | "upsert" | "delete" | "increment" | "decrement"
      collectionId: string
      documentId: string
      data?: Record<string, unknown>
      column?: string
      value?: number
      min?: number  // decrement floor
      max?: number  // increment ceiling
      // Optimistic lock for `update` ops — server pre-checks $updatedAt and
      // 409s the WHOLE transaction on mismatch (D1 seat-race guard).
      expectedUpdatedAt?: string
    }>
  ): Promise<{ success: boolean; count: number; transactionId: string }> {
    return this.request({ action: "transaction", databaseId, operations })
  }

  /** Update multiple documents in a single collection at once. Max 100. */
  async bulkUpdate(
    databaseId: string, collectionId: string,
    documents: Array<{ documentId: string; data: Record<string, unknown> }>
  ): Promise<void> {
    return this.request({ action: "bulk_update", databaseId, collectionId, documents })
  }

  /** Delete multiple documents in a single collection at once. Max 100. */
  async bulkDelete(
    databaseId: string, collectionId: string, documentIds: string[]
  ): Promise<void> {
    return this.request({ action: "bulk_delete", databaseId, collectionId, documentIds })
  }
}

export const databases = new DatabasesProxy()

/** Clear browser SDK state on sign-out */
export function clearBrowserAuth() {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("cookieFallback")
  }
}

// Verify Appwrite connection (call this on app startup)
export async function pingAppwrite() {
  try {
    // Use a simple API call to verify connection
    const response = await fetch(`${ENDPOINT}/health`, {
      method: 'GET',
      headers: {
        'X-Appwrite-Project': PROJECT_ID,
      }
    })
    if (response.ok) {
      return true
    }
    throw new Error('Health check failed')
  } catch {
    return false
  }
}

export const DATABASE_ID = DATABASE_ID_RAW

export const COLLECTIONS = {
  // Core Collections
  SUITES: "suites",
  SUITE_OWNERSHIP_HISTORY: "suite_ownership_history",
  SUITE_EVENTS: "suite_events",
  TASKS: "tasks",
  FURNITURE_ITEMS: "furniture_items",
  FURNITURE_MOVEMENTS: "furniture_movements",
  RESIDENTS: "residents",
  STAFF: "staff",
  PROFILES: "profiles",

  // Billing & AR
  CHARGES: "charges",
  PAYMENTS: "payments",
  PAYMENT_APPLICATIONS: "payment_applications",
  RECURRING_BILLING_RULES: "recurring_billing_rules",

  // Resident lifecycle
  RESIDENT_STATUS_EVENTS: "resident_status_events",

  // Community Relations / CRM
  LEADS: "leads",
  LEAD_ACTIVITIES: "lead_activities",

  // Menu & Orders
  MENU_ITEMS: "menu_items",
  ORDERS: "orders",
  ORDER_ITEMS: "order_items",

  // Reservations & Suite Service
  RESERVATIONS: "reservations",
  ROOM_SERVICE_ORDERS: "room_service_orders", // Collection name kept for backward compatibility

  // Host Workflow (Attendance & Server Assignments)
  ATTENDANCE_RECORDS: "attendance_records",
  SERVER_ASSIGNMENTS: "server_assignments",
  ROLL_CALL: "roll_call",

  // Wellness & Safety
  WELLNESS_CHECKS: "wellness_checks",
  MEAL_ROSTERS: "meal_rosters",
  SICK_AWAY_LIST: "sick_away_list",
  MEAL_PERIODS: "meal_periods",
  SEATING_HISTORY: "seating_history",
  RESIDENT_NOTES: "resident_notes", // Timestamped audit trail for resident status
  MEAL_AWAY_RECORDS: "meal_away_records", // Per-meal away tracking (CEO requirement)

  // Ticketing System
  GUEST_TICKETS: "guest_tickets",
  ALCOHOL_TICKETS: "alcohol_tickets",
  STAFF_MEALS: "staff_meals",

  // Financial
  CASH_OUT_SHEETS: "cash_out_sheets",

  // Application Settings (key-value store for app config)
  APP_SETTINGS: "app_settings",

  // Dining Area & Table Management
  DINING_AREAS: "dining_areas",
  TABLES: "tables",
  SECTIONS: "sections",
  TABLE_SESSIONS: "table_sessions",

  // PTS Cards & Food Sales (CEO Requirement)
  PTS_CARDS: "pts_cards",
  // Named monotonic sequences (one row per sequence, integer `value`).
  // Allocated atomically server-side via the `next_sequence` proxy action.
  COUNTERS: "counters",
  CATERING_REQUESTS: "catering_requests",
  FOOD_SALES: "food_sales",
  FOOD_TYPES: "food_types",

  // Resident Management Enhancements (CEO Requirement)
  INSURANCE_RECORDS: "insurance_records",
  KEY_RECORDS: "key_records",

  // Special Events
  SPECIAL_EVENT_MENUS: "special_event_menus",
  EVENT_SEATINGS: "event_seatings",

  // Inventory Management (CEO Requirement - Slide 14)
  INVENTORY: "inventory",
  INVENTORY_LOG: "inventory_log",

  // Manager Override Tracking (CEO Requirement Slide 14)
  OVERRIDE_LOG: "override_log",

  // Manual Payments (CEO Requirement Slide 11)
  MANUAL_PAYMENTS: "manual_payments",

  // Activity Logging & Audit Trail
  ACTIVITY_LOGS: "activity_logs",

  // Server Table Selections (per-shift table assignments visible to team lead)
  SERVER_TABLE_SELECTIONS: "server_table_selections",

  // Resident Sub-Tables (normalized from residents collection)
  RESIDENT_CONTACTS: "resident_contacts",
  RESIDENT_EMERGENCY_CONTACTS: "resident_emergency_contacts",
  RESIDENT_OCCUPANCY: "resident_occupancy",
  RESIDENT_ASSETS: "resident_assets",
  RESIDENT_PETS: "resident_pets",
  RESIDENT_PRIVACY: "resident_privacy",
  RESIDENT_MEDICAL: "resident_medical",
  RESIDENT_MOVE_IN_CHECKLISTS: "resident_move_in_checklists",

  // Incident Reports (formal reports tied to resident permanent files)
  INCIDENT_REPORTS: "incident_reports",
  INCIDENT_FOLLOW_UPS: "incident_follow_ups", // Page 2 (Executive Director sign-off)
  INCIDENT_IMAGES: "incident_images",         // One row per attached image (1:N to incident_reports)
  INCIDENT_NON_RESIDENTS: "incident_non_residents", // 1:N contacts for non-residents named on a report

  // Front Desk Reports Module (Phase B–D)
  KEY_ISSUANCE: "key_issuance",
  RESIDENT_PENDANT_TRAINING: "resident_pendant_training",

  // Activities & Bus Trips (Module 4 — Post-Dining Expansion)
  ACTIVITIES: "activities",
  ACTIVITY_SIGNUPS: "activity_signups",
  BUS_TRIPS: "bus_trips",
  BUS_MANIFEST_ENTRIES: "bus_manifest_entries",

  // In-App Notification Inbox
  INBOX_MESSAGES: "inbox_messages",
  NOTIFICATION_PREFERENCES: "notification_preferences",

  // Staff "Report a Problem" intake (durable copy of the emailed reports,
  // written server-side by app/api/report-problem/route.ts)
  PROBLEM_REPORTS: "problem_reports",
} as const
